import React, { useEffect, useRef, useState } from 'react';
import { fi } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import { Helmet } from 'react-helmet';
import { Button, Container, Form } from 'react-bootstrap';
import { Loader, SectionHeader, addStatusToast } from '../../common/components';
import { useAppDispatch } from '../../app/hooks';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import {
  DateRange,
  GetSalesStatisticsResponse,
  SalesStatisticsFilter,
  getSalesStatisticsAsync,
} from './statisticsSlice';
import {
  RegionListItem,
  RegionsFilter,
  getRegionsAsync,
} from '../regions/regionsSlice';
import {
  SkiResortListItem,
  getSkiResortsAsync,
  SiteListItem,
} from '../skiResorts/skiResortsSlice';
import {
  MunicipalityListItem,
  getMunicipalitiesAsync,
} from '../municipalities/municipalitiesSlice';
import {
  GroupsFilter,
  getAllCustomerGroupsAsync,
} from '../Users/identityUsersSlice';
import {
  BookingType,
  bookingTypeTranslations,
} from '../bookings/Constants/BookingTypeEnum';
import { useLocale } from '../../locales/useLocale';
import { useAuthUser } from '../auth/authHooks';

function SalesStatistics() {
  const dispatch = useAppDispatch();
  const byCountry = useRef<HTMLInputElement>();
  const byService = useRef<HTMLInputElement>();
  const parseCountryInfo = useRef<HTMLInputElement>();
  const byMonth = useRef<HTMLInputElement>();
  const byAgent = useRef<HTMLInputElement>();
  const [byIh, setByIh] = useState<boolean>(false);
  const byIhYllas = useRef<HTMLInputElement>();
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [accommodationCreatedFrom, setAccommodationCreatedFrom] =
    useState<Date | null>(null);
  const [accommodationCreatedTo, setAccommodationCreatedTo] =
    useState<Date | null>(null);
  const [bookingStartsFrom, setBookingStartsFrom] = useState<Date | null>(null);
  const [bookingStartsTo, setBookingStartsTo] = useState<Date | null>(null);
  const [bookingEndsFrom, setBookingEndsFrom] = useState<Date | null>(null);
  const [bookingEndsTo, setBookingEndsTo] = useState<Date | null>(null);
  const bookingNumber = useRef<HTMLInputElement>();
  const accommodationNumber = useRef<HTMLInputElement>();
  const customerNumber = useRef<HTMLInputElement>();
  const agentNumber = useRef<HTMLInputElement>();
  const municipality = useRef<HTMLInputElement>();
  const bookingTypeOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 13];
  const orderItemTypeOptions = [
    'AccommodationBookingOrderItem',
    'AddOnBookingOrderItem',
    'OfficeFeeBookingOrderItem',
  ];
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState<
    string[]
  >([]);

  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const { activeSite } = useAuthUser();

  const [selectedSites, setSelectedSites] = useState<string[]>(() =>
    activeSite?.id ? [activeSite.id] : [],
  );

  const [selectedSkiResorts, setSelectedSkiResorts] = useState<string[]>([]);

  const [selectedBookingTypes, setSelectedBookingTypes] = useState<number[]>([
    1, 2, 3, 4, 5, 6, 8, 9, 10,
  ]);

  const [selectedOrderItemTypes, setSelectedOrderItemTypes] =
    useState<string[]>(orderItemTypeOptions);

  const [loading, setLoading] = useState<boolean>(false);

  const [municipalityItems, setMunicipalityItems] =
    useState<RegionListItem[]>();
  const [regionItems, setRegionItems] = useState<RegionListItem[]>();
  const [skiResortItems, setSkiResortItems] = useState<SkiResortListItem[]>();
  const [siteItems, setSiteItems] = useState<SiteListItem[]>();
  const [customerGroupItems, setCustomerGroupItems] =
    useState<SkiResortListItem[]>();

  // Local UI state for simple popover panels (to avoid react-bootstrap overlays which use findDOMNode)
  const [showBookingType, setShowBookingType] = useState(false);
  const [showOrderItemType, setShowOrderItemType] = useState(false);
  const [showCustomerGroups, setShowCustomerGroups] = useState(false);
  const [showRegions, setShowRegions] = useState(false);
  const [showSkiResorts, setShowSkiResorts] = useState(false);
  const [showSites, setShowSites] = useState(false);

  const sortByName = (a, b): number => {
    const nameA = a.name.toUpperCase(); // ignore upper and lowercase
    const nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  };

  useEffect(() => {
    // declare the data fetching function
    const fetchData = async () => {
      const filter: RegionsFilter = {
        PageNumber: 1,
        PageSize: 1000,
        searchString: '',
      };

      dispatch(getMunicipalitiesAsync(filter))
        .then(action => {
          const items = action.payload.data as RegionListItem[];
          const sorted = Array.from(items.filter(f => f.name)).sort((a, b) =>
            sortByName(a, b),
          );
          setMunicipalityItems(sorted);
        })
        .catch(error => {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Kuntien haku ei onnistunut`,
            }),
          );
        });

      dispatch(getRegionsAsync(filter))
        .then(action => {
          const items = action.payload.data as RegionListItem[];
          const sorted = Array.from(items).sort((a, b) => sortByName(a, b));
          setRegionItems(sorted);
        })
        .catch(error => {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Maakuntien haku ei onnistunut`,
            }),
          );
        });
      setSiteItems([
        {
          id: '7BF9E2E4-8567-4EF4-84EE-E38B23B72499',
          name: 'LOMARENGAS',
        },
        {
          id: '9826CA69-42DA-4174-B125-E9B29DC327F2',
          name: 'YLLÄS',
        },
        {
          id: '9ECB7F14-13A6-4F29-A3EE-0FCF1977DAEF',
          name: 'SUOMU',
        },
      ]);
      dispatch(getSkiResortsAsync(filter))
        .then(action => {
          const items = action.payload.data as SkiResortListItem[];
          const sorted = Array.from(items).sort((a, b) => sortByName(a, b));
          setSkiResortItems(sorted);
        })
        .catch(error => {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Hiihtokeskusten haku ei onnistunut`,
            }),
          );
        });

      const groupsfilter: GroupsFilter = {
        PageNumber: 1,
        PageSize: 1000,
        SearchString: '',
        Culture: 'fi_FI',
      };

      dispatch(getAllCustomerGroupsAsync(groupsfilter))
        .then(action => {
          const items = [] as SkiResortListItem[];
          action.payload.data.forEach(item => {
            const translation = item.translations.find(
              f => f.keyId === 'CustomerGroupName' && f.locale === 'fi_FI',
            );

            items.push({
              id: item.id,
              name: translation?.text,
            });
          });

          const sorted = Array.from(items).sort((a, b) => sortByName(a, b));
          setCustomerGroupItems(sorted);
        })
        .catch(error => {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Asiakasryhmien haku ei onnistunut`,
            }),
          );
        });
    };

    // call the function
    fetchData()
      // make sure to catch any error
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (activeSite?.id && selectedSites.length === 0) {
      setSelectedSites([activeSite.id]);
    }
  }, [activeSite?.id, selectedSites.length]);

  const customerGroupsChange = (value: string) => {
    let selected = [...selectedCustomerGroups];

    if (selectedCustomerGroups.includes(value)) {
      selected = selected.filter(f => f !== value);
    } else {
      selected.push(value);
    }

    setSelectedCustomerGroups(selected);
  };

  const regionsChange = (value: string) => {
    let selected = [...selectedRegions];

    if (selectedRegions.includes(value)) {
      selected = selected.filter(f => f !== value);
    } else {
      selected.push(value);
    }

    setSelectedRegions(selected);
  };

  const skiResortsChange = (value: string) => {
    let selected = [...selectedSkiResorts];

    if (selectedSkiResorts.includes(value)) {
      selected = selected.filter(f => f !== value);
    } else {
      selected.push(value);
    }

    setSelectedSkiResorts(selected);
  };

  const sitesChange = (value: string) => {
    let selected = [...selectedSites];

    if (selectedSites.includes(value)) {
      selected = selected.filter(f => f !== value);
    } else {
      selected.push(value);
    }

    setSelectedSites(selected);
  };

  const bookingTypeOptionsChange = (value: number) => {
    let selected = [...selectedBookingTypes];

    if (selectedBookingTypes.includes(value)) {
      selected = selected.filter(f => f !== value);
    } else {
      selected.push(value);
    }

    setSelectedBookingTypes(selected);
  };

  const orderItemTypeOptionsChange = (value: string) => {
    let selected = [...selectedOrderItemTypes];

    if (selectedOrderItemTypes.includes(value)) {
      selected = selected.filter(f => f !== value);
    } else {
      selected.push(value);
    }

    setSelectedOrderItemTypes(selected);
  };
  const { currentLocale } = useLocale();
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';
  function getBookingTypeTranslation(
    bookingType: BookingType,
    locale: 'en' | 'fi',
  ): string {
    return bookingTypeTranslations[locale][bookingType] || 'Tuntematon';
  }
  const getBookingStatusName = (value: number) => {
    return getBookingTypeTranslation(value, currentLanguage);
    // switch (value) {
    //   case 0: {
    //     return 'Preliminary';
    //   }
    //   case 1: {
    //     return 'Normal';
    //   }
    //   case 2: {
    //     return 'TravelAgency';
    //   }
    //   case 3: {
    //     return 'UnConfirmedInternet';
    //   }
    //   case 4: {
    //     return 'ConfirmedInternet';
    //   }
    //   case 5: {
    //     return 'OwnerOwn';
    //   }
    //   case 6: {
    //     return 'OwnerReported';
    //   }
    //   case 7: {
    //     return 'PreliminaryGroup';
    //   }
    //   case 8: {
    //     return 'ConfirmedGroup';
    //   }
    //   case 9: {
    //     return 'UnConfirmedInternetOwnerSite';
    //   }
    //   case 10: {
    //     return 'ConfirmedInternetOwnerSite';
    //   }
    //   case 13: {
    //     return 'TemporaryDisabled';
    //   }
    //   case 14: {
    //     return 'None';
    //   }
    //   case 15: {
    //     return 'Draft';
    //   }
    //   case 16: {
    //     return 'Maintenance';
    //   }
    //   default: {
    //     return value.toString();
    //   }
    // }
  };

  const getOrderItemTypeName = (value: string) => {
    switch (value) {
      case 'AccommodationBookingOrderItem': {
        return 'Majoitus';
      }
      case 'AddOnBookingOrderItem': {
        return 'Lisäpalvelut';
      }
      case 'OfficeFeeBookingOrderItem': {
        return 'Toimitus';
      }
      default: {
        return value;
      }
    }
  };

  const getDateString = (value: Date) => {
    const y = value.getFullYear();
    let m = (value.getMonth() + 1).toString();
    let d = value.getDate().toString();

    m = m.padStart(2, '0');
    d = d.padStart(2, '0');

    return `${y}-${m}-${d}`;
  };
  const loadStatistics = async () => {
    setLoading(true);
    const filter = {
      byCountry: byCountry.current.checked,
      byMonth: byMonth.current.checked,
      byService: byService.current.checked,
      parseCountryInfo:
        parseCountryInfo != null &&
        parseCountryInfo.current != null &&
        parseCountryInfo.current.checked != null
          ? parseCountryInfo.current.checked
          : false,
      byAgent: byAgent.current.checked,
      byIH: byIh,
      byIHYllas: byIhYllas.current.checked,
    } as SalesStatisticsFilter;

    if (createdFrom) {
      filter.created ??= {};
      filter.created.from = getDateString(createdFrom);
    }

    if (createdTo) {
      filter.created ??= {};
      filter.created.to = getDateString(createdTo);
    }

    if (accommodationCreatedFrom) {
      filter.accommodationCreated ??= {};
      filter.accommodationCreated.from = getDateString(
        accommodationCreatedFrom,
      );
    }

    if (accommodationCreatedTo) {
      filter.accommodationCreated ??= {};
      filter.accommodationCreated.to = getDateString(accommodationCreatedTo);
    }

    if (bookingStartsFrom) {
      filter.bookingStarts ??= {};
      filter.bookingStarts.from = getDateString(bookingStartsFrom);
    }

    if (bookingStartsTo) {
      filter.bookingStarts ??= {};
      filter.bookingStarts.to = getDateString(bookingStartsTo);
    }

    if (bookingEndsFrom) {
      filter.bookingEnds ??= {};
      filter.bookingEnds.from = getDateString(bookingEndsFrom);
    }

    if (bookingEndsTo) {
      filter.bookingEnds ??= {};
      filter.bookingEnds.to = getDateString(bookingEndsTo);
    }

    if (bookingNumber.current.value) {
      filter.bookingNumber = Number(bookingNumber.current.value);
    }

    if (accommodationNumber.current.value) {
      filter.accommodationNumber = Number(accommodationNumber.current.value);
    }

    if (customerNumber.current.value) {
      filter.customerNumber = Number(customerNumber.current.value);
    }

    if (agentNumber.current.value) {
      filter.agentNumber = Number(agentNumber.current.value);
    }

    if (municipality.current.value) {
      filter.municipality = municipality.current.value;
    }

    if (selectedBookingTypes.length > 0) {
      filter.bookingType = [...selectedBookingTypes];
    }

    if (selectedOrderItemTypes.length > 0) {
      filter.orderItemType = [...selectedOrderItemTypes];
    }

    if (selectedRegions.length > 0) {
      filter.regions = [...selectedRegions];
    }

    if (selectedSkiResorts.length > 0) {
      filter.skiResorts = [...selectedSkiResorts];
    }

    if (selectedSites.length > 0) {
      filter.crossSiteIds = [...selectedSites];
    }

    if (selectedCustomerGroups.length > 0) {
      filter.customerGroups = [...selectedCustomerGroups];
    }

    dispatch(getSalesStatisticsAsync(filter))
      .then(action => {
        setLoading(false);
        const response = action.payload as GetSalesStatisticsResponse;

        if (response.status === 200) {
          const link = document.createElement('a');
          link.href = response.blobUrl;
          link.setAttribute('download', response.fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        } else if (response.status === 204) {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Hakuehdoilla ei löytynyt varauksia`,
            }),
          );
        } else {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Koontitilaston lataus ei onnistunut`,
            }),
          );
        }
      })
      .catch(error => {
        setLoading(false);
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            IsFailed: true,
            Message: `Koontitilaston lataus ei onnistunut`,
          }),
        );
      });
  };

  return (
    <>
      <Helmet>
        <title>Koontitilastot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Koontitilastot']}
          Title="Koontitilastot"
        />

        <div className="bg-white p-5 position-relative">
          {!municipalityItems &&
            !skiResortItems &&
            !regionItems &&
            !customerGroupItems && (
              <div className="position-absolute top-0 left-0 z-3 w-100 h-100 bg-white opacity-50 d-flex justify-content-center align-items-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

          <div className="row gx-48 gy-24">
            <div className="col-12">
              <input
                type="radio"
                id="month"
                name="statisticsType"
                ref={byMonth}
                className="form-check-input"
                defaultChecked
              />
              <Form.Label htmlFor="month" className="form-label ms-2">
                Kuukausi
              </Form.Label>
            </div>
            <div className="col-12">
              <input
                type="radio"
                id="byService"
                name="statisticsType"
                ref={byService}
                className="form-check-input"
              />
              <Form.Label htmlFor="byService" className="form-label ms-2">
                Palvelu
              </Form.Label>
            </div>
            <div className="col-12">
              <input
                type="radio"
                id="country"
                name="statisticsType"
                ref={byCountry}
                className="form-check-input"
              />
              <Form.Label htmlFor="country" className="form-label ms-2">
                Maatilasto
              </Form.Label>
            </div>
            <div className="col-12">
              <input
                type="radio"
                id="agent"
                name="statisticsType"
                ref={byAgent}
                className="form-check-input"
              />
              <Form.Label htmlFor="agent" className="form-label ms-2">
                Edustajatilasto
              </Form.Label>
            </div>
            <div className="col-12">
              <input
                type="checkbox"
                id="onlyinterhome"
                checked={byIh}
                className="form-check-input"
                onChange={e => setByIh(e.target.checked)}
              />
              <Form.Label htmlFor="onlyinterhome" className="form-label ms-2">
                Vain Interhome myynti
              </Form.Label>
            </div>
            {byIh && (
              <div className="col-12">
                <input
                  type="checkbox"
                  id="parseCountryInfo"
                  ref={parseCountryInfo}
                  className="form-check-input"
                />
                <Form.Label
                  htmlFor="parseCountryInfo"
                  className="form-label ms-2"
                >
                  Parsi Interhome Kommenttikenttä
                </Form.Label>
              </div>
            )}
            <div className="col-12">
              <input
                type="checkbox"
                id="onlyihyllas"
                ref={byIhYllas}
                className="form-check-input"
              />
              <Form.Label htmlFor="onlyihyllas" className="form-label ms-2">
                Vain Interhome - Ylläs myynti
              </Form.Label>
            </div>
            <div className="col-6">
              <Form.Label htmlFor="createdFrom" className="form-label">
                Varaus tehty
              </Form.Label>
              <div className="d-flex align-items-center">
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="createdFrom"
                  selected={createdFrom}
                  onChange={setCreatedFrom}
                  className="form-control"
                />
                <strong className="px-1">-</strong>
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="createdTo"
                  selected={createdTo}
                  onChange={setCreatedTo}
                  className="form-control"
                />
              </div>
            </div>
            <div className="col-6">
              <Form.Label
                htmlFor="accommodationCreatedFrom"
                className="form-label"
              >
                Kohde luotu
              </Form.Label>
              <div className="d-flex align-items-center">
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="accommodationCreatedFrom"
                  selected={accommodationCreatedFrom}
                  onChange={setAccommodationCreatedFrom}
                  className="form-control"
                />
                <strong className="px-1">-</strong>
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="accommodationCreatedTo"
                  selected={accommodationCreatedTo}
                  onChange={setAccommodationCreatedTo}
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-6">
              <Form.Label htmlFor="bookingStartsFrom" className="form-label">
                Varaus alkaa
              </Form.Label>
              <div className="d-flex align-items-center">
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="bookingStartsFrom"
                  selected={bookingStartsFrom}
                  onChange={setBookingStartsFrom}
                  className="form-control"
                />
                <strong className="px-1">-</strong>
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="bookingStartsTo"
                  selected={bookingStartsTo}
                  onChange={setBookingStartsTo}
                  className="form-control"
                />
              </div>
            </div>
            <div className="col-6">
              <Form.Label htmlFor="bookingEndsFrom" className="form-label">
                Varaus päättyy
              </Form.Label>
              <div className="d-flex align-items-center">
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="bookingEndsFrom"
                  selected={bookingEndsFrom}
                  onChange={setBookingEndsFrom}
                  className="form-control"
                />
                <strong className="px-1">-</strong>
                <DatePicker
                  locale={fi}
                  required
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  id="bookingEndsTo"
                  selected={bookingEndsTo}
                  onChange={setBookingEndsTo}
                  className="form-control"
                />
              </div>
            </div>
            <div className="col-md-6">
              <Form.Label className="form-label">Varauksen tyyppi</Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 text-start"
                  onClick={() => setShowBookingType(v => !v)}
                >
                  {selectedBookingTypes?.length > 0 ? (
                    <span>{selectedBookingTypes.length} valittu</span>
                  ) : (
                    <span>Kaikki</span>
                  )}
                </button>
                {showBookingType && (
                  <div
                    className="bg-white border rounded mt-1 p-1"
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      zIndex: 1000,
                      position: 'absolute',
                      width: '100%',
                    }}
                  >
                    {bookingTypeOptions.map(opt => (
                      <button
                        key={`bto-${opt}`}
                        type="button"
                        className="dropdown-item text-wrap"
                        onClick={() => bookingTypeOptionsChange(opt)}
                      >
                        <div className="form-check">
                          <span>
                            <input
                              type="checkbox"
                              disabled
                              checked={selectedBookingTypes.includes(opt)}
                              className="form-check-input"
                            />{' '}
                            {getBookingStatusName(opt)}{' '}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <Form.Label className="form-label">Varausrivin tyyppi</Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 text-start"
                  onClick={() => setShowOrderItemType(v => !v)}
                >
                  {selectedOrderItemTypes?.length > 0 ? (
                    <span>{selectedOrderItemTypes.length} valittu</span>
                  ) : (
                    <span>Kaikki</span>
                  )}
                </button>
                {showOrderItemType && (
                  <div
                    className="bg-white border rounded mt-1 p-1"
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      zIndex: 1000,
                      position: 'absolute',
                      width: '100%',
                    }}
                  >
                    {orderItemTypeOptions.map(opt => (
                      <button
                        key={`oit-${opt}`}
                        type="button"
                        className="dropdown-item text-wrap"
                        onClick={() => orderItemTypeOptionsChange(opt)}
                      >
                        <div className="form-check">
                          <span>
                            <input
                              type="checkbox"
                              disabled
                              checked={selectedOrderItemTypes.includes(opt)}
                              className="form-check-input"
                            />{' '}
                            {getOrderItemTypeName(opt)}{' '}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col">
              <Form.Label htmlFor="accommodationNumber" className="form-label">
                Kohteen numero
              </Form.Label>
              <input
                type="text"
                id="accommodationNumber"
                className="form-control"
                ref={accommodationNumber}
              />
            </div>
            <div className="col">
              <Form.Label htmlFor="bookingNumber" className="form-label">
                Varausnumero
              </Form.Label>
              <input
                type="text"
                id="bookingNumber"
                className="form-control"
                ref={bookingNumber}
              />
            </div>
            <div className="col">
              <Form.Label htmlFor="customerNumber" className="form-label">
                Asiakasnumero
              </Form.Label>
              <input
                type="text"
                id="customerNumber"
                className="form-control"
                ref={customerNumber}
              />
            </div>
            <div className="col">
              <Form.Label htmlFor="agentNumber" className="form-label">
                Asiamiesnumero
              </Form.Label>
              <input
                type="text"
                id="agentNumber"
                className="form-control"
                ref={agentNumber}
              />
            </div>
            <div className="col">
              <Form.Label className="form-label">Asiakasryhmät</Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 text-start"
                  onClick={() => setShowCustomerGroups(v => !v)}
                >
                  <span>Kaikki</span>
                </button>
                {showCustomerGroups && (
                  <div
                    className="bg-white border rounded mt-1 p-1"
                    style={{
                      maxHeight: '500px',
                      overflow: 'auto',
                      zIndex: 1000,
                      position: 'absolute',
                      width: '100%',
                    }}
                  >
                    {customerGroupItems?.length > 0 &&
                      customerGroupItems.map(opt => (
                        <button
                          key={`oit-${opt.id}`}
                          type="button"
                          className="dropdown-item text-wrap"
                          onClick={() => customerGroupsChange(opt.id)}
                        >
                          <div className="form-check">
                            <span>
                              <input
                                type="checkbox"
                                disabled
                                checked={selectedCustomerGroups.includes(
                                  opt.id,
                                )}
                                className="form-check-input"
                              />{' '}
                              {opt.name}{' '}
                            </span>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div className="w-100 my-n24" />
            <div className="col-md-4">
              <Form.Label htmlFor="municipality" className="form-label">
                Kunta
              </Form.Label>
              <input
                type="text"
                id="municipality"
                className="form-control"
                ref={municipality}
                list="municipalitylist"
              />
              <datalist id="municipalitylist">
                {municipalityItems?.length > 0 &&
                  municipalityItems.map(opt => {
                    return <option key={opt.id}>{opt.name}</option>;
                  })}
              </datalist>
            </div>
            <div className="col-md-4">
              <Form.Label className="form-label">Maakunnat</Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 text-start"
                  onClick={() => setShowRegions(v => !v)}
                >
                  {selectedRegions?.length > 0 ? (
                    <span>{selectedRegions.length} valittu</span>
                  ) : (
                    <span>Kaikki</span>
                  )}
                </button>
                {showRegions && (
                  <div
                    className="bg-white border rounded mt-1 p-1"
                    style={{
                      maxHeight: '500px',
                      overflow: 'auto',
                      zIndex: 1000,
                      position: 'absolute',
                      width: '100%',
                    }}
                  >
                    {regionItems?.length > 0 &&
                      regionItems.map(opt => (
                        <button
                          key={`oit-${opt.id}`}
                          type="button"
                          className="dropdown-item text-wrap"
                          onClick={() => regionsChange(opt.id)}
                        >
                          <div className="form-check">
                            <span>
                              <input
                                type="checkbox"
                                disabled
                                checked={selectedRegions.includes(opt.id)}
                                className="form-check-input"
                              />{' '}
                              {opt.name}{' '}
                            </span>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <Form.Label className="form-label">Hiihtokeskukset</Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 text-start"
                  onClick={() => setShowSkiResorts(v => !v)}
                >
                  {selectedSkiResorts?.length > 0 ? (
                    <span>{selectedSkiResorts.length} valittu</span>
                  ) : (
                    <span>Kaikki</span>
                  )}
                </button>
                {showSkiResorts && (
                  <div
                    className="bg-white border rounded mt-1 p-1"
                    style={{
                      maxHeight: '500px',
                      overflow: 'auto',
                      zIndex: 1000,
                      position: 'absolute',
                      width: '100%',
                    }}
                  >
                    {skiResortItems?.length > 0 &&
                      skiResortItems.map(opt => (
                        <button
                          key={`oit-${opt.id}`}
                          type="button"
                          className="dropdown-item text-wrap"
                          onClick={() => skiResortsChange(opt.id)}
                        >
                          <div className="form-check">
                            <span>
                              <input
                                type="checkbox"
                                disabled
                                checked={selectedSkiResorts.includes(opt.id)}
                                className="form-check-input"
                              />{' '}
                              {opt.name}{' '}
                            </span>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <Form.Label className="form-label">Varaamot</Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 text-start"
                  onClick={() => setShowSites(v => !v)}
                >
                  {selectedSites?.length > 0 ? (
                    <span>{selectedSites.length} valittu</span>
                  ) : (
                    <span>Kaikki</span>
                  )}
                </button>
                {showSites && (
                  <div
                    className="bg-white border rounded mt-1 p-1"
                    style={{
                      maxHeight: '500px',
                      overflow: 'auto',
                      zIndex: 1000,
                      position: 'absolute',
                      width: '100%',
                    }}
                  >
                    {siteItems?.length > 0 &&
                      siteItems.map(opt => (
                        <button
                          key={`oit-${opt.id}`}
                          type="button"
                          className="dropdown-item text-wrap"
                          onClick={() => sitesChange(opt.id)}
                        >
                          <div className="form-check">
                            <span>
                              <input
                                type="checkbox"
                                disabled
                                checked={selectedSites.includes(opt.id)}
                                className="form-check-input"
                              />{' '}
                              {opt.name}{' '}
                            </span>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-32 d-flex align-items-center">
            <button
              type="button"
              onClick={loadStatistics}
              className="btn btn-primary"
              disabled={false}
            >
              <span>Lataa</span>
            </button>
            {loading && (
              <div
                className="spinner-border spinner-border-sm ms-3"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
          </div>
        </div>
      </Container>
    </>
  );
}

export default SalesStatistics;
